/**
 * MyStore — reseller's business surface ("Мой магазин").
 *
 * Replaces the bare Dashboard render in the chat-area zone when the
 * reseller has nothing else selected. Houses the four business
 * surfaces a reseller actually works in:
 *
 *   • Главная   — KPI + pending orders + recent CRM activity
 *   • Клиенты   — CRM list + per-customer detail
 *   • Пересылка — FX rate, caption template, forward targets
 *   • Аналитика — forwards-per-reseller + top cards + Pulse
 *
 * The tab bodies are lifted from SettingsModal (where they used to
 * live as tabs) and imported here. SettingsModal is now purely
 * account-level: Profile, Password, Каналы, Premium.
 *
 * Props:
 *   user, channels         — passed through to Dashboard / sub-tabs
 *   initialTab             — open on a specific tab (used by callers
 *                            like the SetupChecklist banner that
 *                            previously called openForwardSettings).
 *   onOpenOrders           — open OrdersModal
 *   onOpenPremium          — open SettingsModal directly on the
 *                            premium tab (for the analytics upgrade
 *                            prompt)
 *   onBrowseChannels       — switch sidebar to channels tab
 *   isMobile               — render the mobile hamburger header
 *   onOpenSidebar          — slide the mobile sidebar in
 *   toast                  — passed through to the sub-tabs
 */
import { useState, useEffect } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Mono, Serif } from "./ui.jsx";
import { Dashboard } from "./Dashboard.jsx";
import { SetupChecklist } from "./SetupChecklist.jsx";
import { OnboardingWizard } from "./OnboardingWizard.jsx";
import { CuratedPanel } from "./CuratedPanel.jsx";
import { BatchesPanel } from "./BatchesPanel.jsx";
import { AdminPanel } from "./AdminPanel.jsx";
import { useT } from "../i18n.jsx";
import {
  CustomersTab,
  ForwardSettingsTab,
  ForwardStatsTab,
} from "./SettingsModal.jsx";

const TAB_IDS = ["home", "customers", "batches", "forward", "curated", "stats"];
const TAB_LABEL_KEY = {
  home:      "mystore.tab.home",
  customers: "mystore.tab.customers",
  batches:   "mystore.tab.batches",
  forward:   "mystore.tab.forward",
  curated:   "mystore.tab.curated",
  stats:     "mystore.tab.stats",
};

export function MyStore({
  user,
  channels = [],
  initialTab = "home",
  isMobile = false,
  onOpenSidebar,
  onOpenOrders,
  onOpenCustomers,    // legacy — kept for Dashboard's section-action arrow
  onOpenPremium,
  onOpenNotifications,
  onBrowseChannels,
  onOpenForwardSettings, // legacy — kept for Dashboard's first-run CTA
  // Staff-only — passed through to AdminPanel, which is rendered
  // inline for admin/manager users (no longer a separate modal).
  users = [],
  groups = [],
  onRefresh,
  onCreateGroup,
  onAddTopic,
  onOpenDm,
  toast,
}) {
  const t = useT();
  const [tab, setTab] = useState(initialTab || "home");
  // Staff (admin / manager) get a queue-focused landing — the
  // reseller-oriented "Главная / Клиенты / Пересылка / Закупки /
  // Аналитика" tabs don't apply to them and shipping that UI to staff
  // creates clutter (and a misleading "Доброй ночи, Ruslan" reseller
  // dashboard for the admin operator).
  const isStaff = user?.role === "admin" || user?.role === "manager";
  const visibleTabs = isStaff ? [] : TAB_IDS.map((id) => ({ id, label: t(TAB_LABEL_KEY[id]) }));
  // First-run onboarding wizard. Triggers exactly once when:
  //   • role === customer
  //   • localStorage flag bg_onboarding_done:<uid> is not set
  //   • reseller-settings has zero configured forward targets
  // We check the third condition lazily so customers who set up via
  // the Settings UI before the wizard existed never see it pop up.
  const [wizardOpen, setWizardOpen] = useState(false);
  useEffect(() => {
    if (!user || user.role !== "customer") return;
    let cancelled = false;
    (async () => {
      try {
        const flag = localStorage.getItem(
          `bg_onboarding_done:${user.uid}`);
        if (flag === "1") return;
        const s = await api.getResellerSettings();
        if (cancelled) return;
        const hasTarget =
          Array.isArray(s.forwardTargets) &&
          s.forwardTargets.some((t) => t && t.chatId);
        if (!hasTarget) setWizardOpen(true);
      } catch {
        // Network/auth — skip; user can still complete via SetupChecklist.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Staff (admin / manager) get AdminPanel rendered inline — same
  // component the old modal-Admin-button used to open, but now with
  // a fresh "Главная" overview tab that replaces the prior separate
  // AdminHome. Single surface, no modal-on-top redundancy.
  if (isStaff) {
    return (
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        background: T_NEW.paper, minHeight: 0,
      }}>
        {isMobile && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "calc(8px + env(safe-area-inset-top, 0px)) 12px 8px",
            background: T_NEW.paperRaised,
            borderBottom: `1px solid ${T_NEW.ink12}`,
          }}>
            <button onClick={onOpenSidebar}
                    style={{ background: "transparent", border: "none",
                             padding: "6px 8px", cursor: "pointer",
                             fontSize: 22, lineHeight: 1, color: T_NEW.ink }}>
              ☰
            </button>
            <Mono color={T_NEW.persimmon} style={{ fontWeight: 700 }}>
              {t("mystore.adminHeading")}
            </Mono>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto",
                      minHeight: 0,
                      minWidth: 0,         // forbid child intrinsic widths from expanding us
                      maxWidth: "100vw",   // hard viewport clamp; immune to parent quirks
                      overflowX: "hidden", // hard clip if anything still tries
                      padding: "16px 16px 40px",
                      boxSizing: "border-box",
                      position: "relative" }}>
          {/* AdminPanel self-caps at its editorial width — see
              its root div. We just provide the scroll container +
              outer padding here. */}
          <AdminPanel
            user={user}
            users={users}
            groups={groups}
            onRefresh={onRefresh}
            onCreateGroup={onCreateGroup}
            onAddTopic={onAddTopic}
            onOpenDm={onOpenDm}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      background: T_NEW.paper,
      minHeight: 0,
    }}>
      {/* Mobile header — hamburger + section label */}
      {isMobile && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "calc(8px + env(safe-area-inset-top, 0px)) 12px 8px",
          background: T_NEW.paperRaised,
          borderBottom: `1px solid ${T_NEW.ink12}`,
          flexShrink: 0,
        }}>
          <button
            onClick={onOpenSidebar}
            aria-label={t("mystore.menu")}
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              cursor: "pointer",
              fontSize: 22, lineHeight: 1,
              color: T_NEW.ink,
            }}>
            ☰
          </button>
          <Mono color={T_NEW.persimmon} style={{ fontWeight: 700 }}>
            {t("mystore.heading")}
          </Mono>
        </div>
      )}

      {/* Tabs strip — desktop and mobile. Horizontal-scroll on narrow
          so labels never truncate. Active state uses the same ink-on-
          paper inversion as the Settings Tabs primitive for visual
          consistency. */}
      <div style={{
        display: "flex",
        gap: 2,
        padding: "12px 16px 0",
        background: T_NEW.paper,
        borderBottom: `1px solid ${T_NEW.ink12}`,
        overflowX: "auto",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        flexShrink: 0,
      }}
           className="cg-tabs-scroll">
        {visibleTabs.map((tt) => {
          const active = tt.id === tab;
          return (
            <button key={tt.id} onClick={() => setTab(tt.id)}
                    style={{
                      padding: "10px 14px 12px",
                      border: "none",
                      borderBottom: active
                        ? `2px solid ${T_NEW.persimmon}`
                        : `2px solid transparent`,
                      background: "transparent",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      fontFamily: T_NEW.fontMono,
                      fontSize: T_NEW.micro,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: T_NEW.monoTrack,
                      color: active ? T_NEW.persimmon : T_NEW.ink56,
                      transition: "color 160ms",
                      marginBottom: -1,
                    }}>
              {tt.label}
            </button>
          );
        })}
      </div>

      {/* Onboarding nudge — slim banner that disappears once the
          reseller has saved an FX rate AND at least one forward
          target. CTA jumps directly to the Пересылка tab below.
          Re-uses the same component the ChannelFeed pre-renders so
          the copy stays consistent across surfaces. */}
      {user?.role === "customer" && (
        <SetupChecklist
          user={user}
          bump={0}
          onOpenForwardSettings={() => setTab("forward")}
        />
      )}

      {/* Tab body — single scroll container per tab. Most tabs already
          have internal padding so the wrapper sticks to a uniform
          16-24px gutter. */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {tab === "home" && (
          /* Dashboard renders its own padding + heading. Suppress its
             mobile hamburger here because MyStore already shows one
             above. */
          <Dashboard
            user={user}
            channels={channels}
            isMobile={false}
            onOpenOrders={onOpenOrders}
            onOpenCustomers={() => setTab("customers")}
            onOpenNotifications={onOpenNotifications}
            onBrowseChannels={onBrowseChannels}
            onOpenForwardSettings={() => setTab("forward")}
          />
        )}
        {tab === "customers" && (
          <div style={{ padding: "20px 16px 40px",
                        maxWidth: 760, margin: "0 auto" }}>
            <Serif size={T_NEW.displayMd}
                   style={{ display: "block", marginBottom: 12,
                            fontWeight: 500 }}>
              {t("mystore.section.customers")}
            </Serif>
            <CustomersTab toast={toast} />
          </div>
        )}
        {tab === "forward" && (
          <div style={{ padding: "20px 16px 40px",
                        maxWidth: 760, margin: "0 auto" }}>
            <Serif size={T_NEW.displayMd}
                   style={{ display: "block", marginBottom: 12,
                            fontWeight: 500 }}>
              {t("mystore.section.forward")}
            </Serif>
            <ForwardSettingsTab toast={toast} />
          </div>
        )}
        {tab === "curated" && (
          <div style={{ padding: "20px 16px 40px",
                        maxWidth: 760, margin: "0 auto" }}>
            <CuratedPanel user={user} />
          </div>
        )}
        {tab === "batches" && (
          <div style={{ padding: "20px 16px 40px",
                        maxWidth: 760, margin: "0 auto" }}>
            <BatchesPanel scope="self" />
          </div>
        )}
        {wizardOpen && (
          <OnboardingWizard
            user={user}
            toast={toast}
            onClose={() => setWizardOpen(false)}
          />
        )}
        {tab === "stats" && (
          <div style={{ padding: "20px 16px 40px",
                        maxWidth: 760, margin: "0 auto" }}>
            <Serif size={T_NEW.displayMd}
                   style={{ display: "block", marginBottom: 12,
                            fontWeight: 500 }}>
              {t("mystore.section.stats")}
            </Serif>
            <ForwardStatsTab
              toast={toast}
              user={user}
              onUpgrade={onOpenPremium}
            />
          </div>
        )}
      </div>
    </div>
  );
}
